import {
  getAppInsights,
  trackChatEvent,
  trackChatException,
} from "../services/appInsightsService";
import { SeverityLevel } from "@microsoft/applicationinsights-web";


// Patterns to exclude from tracking to avoid noise
const EXCLUDE_PATTERNS = [/^\[AppInsights\]/, /^\[CustomConsole\]/];

function shouldTrack(message: string): boolean {
  return !EXCLUDE_PATTERNS.some((pattern) => pattern.test(message));
}

interface WidgetConsole {
  debug: (message?: any, ...optionalParams: any[]) => void;
  log: (message?: any, ...optionalParams: any[]) => void;
  warn: (message?: any, ...optionalParams: any[]) => void;
  error: (message?: any, ...optionalParams: any[]) => void;
}

export function createCustomWidgetConsole(): WidgetConsole {
  const appInsights = getAppInsights();

  if (!appInsights) {
    console.warn(
      "[WidgetConsole] App Insights not initialized, skipping console tracking",
    );
    return { debug: () => {}, log: () => {}, warn: () => {}, error: () => {} };
  }

  const widgetConsole: WidgetConsole = {
    debug: (message?: any, ...optionalParams: any[]): void => {
      console.debug(message, ...optionalParams);
      // Send to telemetry
      const fullMessage = [message, ...optionalParams].join(" ");
      if (shouldTrack(fullMessage)) {
        trackChatEvent("debug", {
          message: fullMessage,
          severityLevel: SeverityLevel.Verbose,
        });
      }
      console.debug(`Sent debug event: ${fullMessage} to App Insights`);
    },
    log: (message?: any, ...optionalParams: any[]): void => {
      console.log(message, ...optionalParams);
      // Send to telemetry
      const fullMessage = [message, ...optionalParams].join(" ");
      if (shouldTrack(fullMessage)) {
        trackChatEvent("log", {
          message: fullMessage,
          severityLevel: SeverityLevel.Information,
        });
      }
      console.log(`Sent log event: ${fullMessage} to App Insights`);
    },
    warn: (message?: any, ...optionalParams: any[]): void => {
      console.warn(message, ...optionalParams);
      // Send to telemetry
      const fullMessage = [message, ...optionalParams].join(" ");
      if (shouldTrack(fullMessage)) {
        trackChatEvent("warn", {
          message: fullMessage,
          severityLevel: SeverityLevel.Warning,
        });
      }
      console.log(`Sent log event: ${fullMessage} to App Insights`);
    },
    error: (message?: any, ...optionalParams: any[]): void => {
      console.error(message, ...optionalParams);
      // Send to telemetry
      const fullMessage = [message, ...optionalParams].join(" ");
      if (shouldTrack(fullMessage)) {
        trackChatException(new Error(fullMessage), {
          severityLevel: SeverityLevel.Error,
        });
      }
      console.log(`Sent log event: ${fullMessage} to App Insights`);
    },
  };

  return widgetConsole;
}